import { useState, useEffect, useMemo, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { TimeField } from "@/components/TimeField";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { todayISO, fmtEUR, fmtDate } from "@/lib/format";
import { fmtDuration as durLabel } from "@/lib/agenda";
import { ChevronDown, ChevronUp, Search, Plus, Trash2, Coins, Gift, Wallet, AlertTriangle, PawPrint } from "lucide-react";
import { coverageForDog, recomputeLinesCoverage } from "./prestationCoverage";

const STATUS = ["Pianificata", "Svolta", "Annullata"];
const DUR_FACTOR = { minutes: 1, hours: 60, days: 1440 };
const UNIT_MIN = { minute: 1, hour: 60, day: 1440 };
const BILLING_LABEL = { minute: "minuto", hour: "ora", day: "giorno" };
const COVERAGES = [["direct", "Pagamento diretto", Wallet], ["package", "Pacchetto", Coins], ["gift", "Omaggio", Gift]];
const round2 = (n) => Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;
const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const creditWord = (n) => (Number(n) === 1 ? "credito" : "crediti");

const svcValue = (price, mode, bunit, qtyMin) => {
  if (mode !== "per_time_unit" || !bunit) return round2(price);
  const u = UNIT_MIN[bunit];
  if (!u) return round2(price);
  return round2(Number(price || 0) * (Number(qtyMin || 0) / u));
};

// price_override is a final amount; applied_price retains the historical tariff.
const appliedAmount = (ln, price, minutes) => price.price_override != null
  ? round2(price.price_override)
  : svcValue(price.applied_price, ln.pricing_mode, ln.billing_unit, minutes);
const lineMinutes = (ln) => (ln.has_duration ? Math.round(Number(ln.dur_value || 0) * (DUR_FACTOR[ln.duration_unit] || 1)) : 0);

const splitLine = (ln) => {
  const qty = lineMinutes(ln);
  let ref = 0, pkg = 0, gift = 0, direct = 0;
  const creds = [];
  if (ln.price_scope === "per_dog") {
    for (const d of ln.dogs || []) {
      const amt = appliedAmount(ln, d, qty);
      ref += amt;
      if (d.coverage === "gift") gift += amt;
      else if (d.coverage === "package") { pkg += amt; creds.push([d.dog_id, Number(d.required_credits || 0)]); }
      else direct += amt;
    }
  } else {
    const amt = appliedAmount(ln, ln, qty);
    ref += amt;
    if (ln.line_coverage === "gift") gift += amt;
    else if (ln.line_coverage === "package" && (ln.dogs || []).length === 1) { pkg += amt; creds.push([ln.dogs[0].dog_id, Number(ln.line_required_credits || 0)]); }
    else direct += amt;
  }
  return { ref: round2(ref), pkg: round2(pkg), gift: round2(gift), direct: round2(direct), creds };
};

const parentDuration = (lines) => {
  const timed = (lines || []).filter((l) => l.service_type_id && l.has_duration && lineMinutes(l) > 0);
  if (!timed.length) return 0;
  const base = lineMinutes(timed[0]);
  const extra = timed.slice(1).filter((l) => l.timing === "extends").reduce((s, l) => s + lineMinutes(l), 0);
  return base + extra;
};

const compositeLabel = (lines) => {
  const configured = (lines || []).filter((l) => l.service_type_id);
  if (!configured.length) return "Prestazione";
  const first = configured[0].service_name;
  const extra = configured.length - 1;
  return extra ? `${first} + ${extra} ${extra === 1 ? "servizio" : "servizi"}` : first;
};

export const CompositePrestationDialog = ({ open, onOpenChange, mode = "prestazione", presetOwnerId, presetDogId, editSession, onSaved }) => {
  const [owners, setOwners] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [types, setTypes] = useState([]);
  const [ownerId, setOwnerId] = useState("");
  const [dogIds, setDogIds] = useState([]);
  const [date, setDate] = useState(todayISO());
  const [startTime, setStartTime] = useState("");
  const [status, setStatus] = useState("Pianificata");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([]);
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [ownerQuery, setOwnerQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [reqKey, setReqKey] = useState("");
  const [pkgByDog, setPkgByDog] = useState({});
  const [applyPrompt, setApplyPrompt] = useState(null); // { dogId }
  const savingRef = useRef(false);

  const isEdit = !!editSession;
  const readOnly = isEdit && editSession.status === "Svolta";

  const activePkg = (dogId) => (pkgByDog[dogId] || []).find((p) => (p.remaining_credits || 0) > 0) || null;
  const defaultCoverage = (dogId, reqCredits) => coverageForDog(pkgByDog[dogId], reqCredits);

  const mkLineFromType = (t, forDogs) => {
    const scope = t.price_scope || "per_booking";
    // A service is package-eligible only if explicitly flagged AND it has a positive default
    // credit cost (mirrors backend authority; legacy 0-credit services are NOT eligible).
    const elig = t.package_eligible !== false && (Number(t.default_credits) || 0) > 0;
    return {
      uid: uid(),
      service_type_id: t.id,
      service_name: t.name,
      price_scope: scope,
      pricing_mode: t.has_duration === false ? "fixed" : (t.pricing_mode || "fixed"),
      billing_unit: t.billing_unit || null,
      has_duration: t.has_duration !== false,
      duration_unit: t.duration_unit || "minutes",
      dur_value: t.has_duration !== false ? round2((t.default_duration_minutes || 60) / (DUR_FACTOR[t.duration_unit || "minutes"] || 1)) : "",
      timing: "included",
      package_eligible: elig,
      default_credits: t.default_credits || 0,
      professional_diary_enabled: t.professional_diary_enabled !== false,
      list_price: t.default_price || 0,
      applied_price: t.default_price || 0,
      line_coverage: (elig && scope !== "per_dog" && (forDogs || []).length === 1) ? defaultCoverage((forDogs || [])[0], t.default_credits || 1) : "direct",
      line_coverage_auto: true,
      line_required_credits: t.default_credits || 1,
      dogs: (forDogs || []).map((id) => ({ dog_id: id, applied_price: t.default_price || 0, coverage: (elig && scope === "per_dog") ? defaultCoverage(id, t.default_credits || 1) : "direct", coverage_auto: true, required_credits: t.default_credits || 1 })),
      expanded: true,
    };
  };

  const emptyLine = (forDogs) => ({
    uid: uid(), service_type_id: "", service_name: "", price_scope: "per_booking",
    pricing_mode: "fixed", billing_unit: null, has_duration: true, duration_unit: "minutes",
    dur_value: "", timing: "included", package_eligible: true, default_credits: 0,
    professional_diary_enabled: true, list_price: 0, applied_price: 0, line_coverage: "direct", line_coverage_auto: true,
    line_required_credits: 1, dogs: (forDogs || []).map((id) => ({ dog_id: id, applied_price: 0, coverage: "direct", coverage_auto: true, required_credits: 1 })),
    expanded: true,
  });

  useEffect(() => {
    if (!open) return;
    Promise.all([api.get("/owners"), api.get("/dogs"), api.get("/service-types"), api.get("/packages")])
      .then(([o, d, t, p]) => {
        setOwners(o.data); setDogs(d.data); setTypes(t.data.filter((x) => x.active && !x.is_package_type));
        const by = {};
        (p.data || []).forEach((pk) => { (by[pk.dog_id] = by[pk.dog_id] || []).push(pk); });
        Object.values(by).forEach((arr) => arr.sort((a, b) => (a.opening_date || "").localeCompare(b.opening_date || "")));
        setPkgByDog(by);
      })
      .catch(() => {});
    setOwnerOpen(false); setOwnerQuery(""); setApplyPrompt(null);
    setReqKey(uid());
    if (editSession) {
      const sl = editSession.service_lines || [];
      setOwnerId(editSession.owner_id);
      setDogIds(editSession.dog_ids || []);
      setDate(editSession.date || todayISO());
      setStartTime(editSession.start_time || "");
      setStatus(editSession.status || "Pianificata");
      setNotes(editSession.notes || "");
      setLines(sl.map((ln) => ({
        uid: uid(),
        service_type_id: ln.service_type_id || "",
        service_name: ln.service_name || "",
        price_scope: ln.price_scope || "per_booking",
        pricing_mode: ln.pricing_mode || "fixed",
        billing_unit: ln.billing_unit || null,
        has_duration: ln.has_duration !== false,
        duration_unit: ln.duration_unit || "minutes",
        dur_value: ln.has_duration !== false ? round2((ln.duration_minutes || 0) / (DUR_FACTOR[ln.duration_unit || "minutes"] || 1)) : "",
        timing: ln.timing || "included",
        package_eligible: ln.package_eligible !== false,
        default_credits: ln.default_credits || 0,
        professional_diary_enabled: ln.professional_diary_enabled !== false,
        list_price: ln.list_price != null ? ln.list_price : (ln.applied_price || 0),
        applied_price: ln.applied_price != null ? ln.applied_price : 0,
        price_override: ln.price_override ?? null,
        line_coverage: ln.line_coverage || "direct",
        line_coverage_auto: false,
        line_required_credits: ln.required_credits || ln.default_credits || 1,
        dogs: (ln.dogs || []).map((d) => ({
          dog_id: d.dog_id,
          applied_price: d.applied_price != null ? d.applied_price : (ln.list_price || 0),
          price_override: d.price_override ?? null,
          coverage: d.coverage || "direct",
          coverage_auto: false,
          required_credits: d.required_credits || ln.default_credits || 1,
        })),
        expanded: false,
      })));
    } else {
      const startDogs = presetDogId ? [presetDogId] : [];
      setOwnerId(presetOwnerId || "");
      setDogIds(startDogs);
      setDate(todayISO());
      setStartTime("");
      setStatus("Pianificata");
      setNotes("");
      setLines([emptyLine(startDogs)]);
    }
  }, [open, editSession, mode, presetOwnerId, presetDogId]);

  const dogsForOwner = dogs.filter((d) => d.owner_id === ownerId);
  const dogName = (id) => (dogs.find((d) => d.id === id) || {}).nome || "";
  const dogsByOwner = (oid) => dogs.filter((d) => d.owner_id === oid).map((d) => d.nome);
  const ownerLabel = (o) => {
    const nm = `${o.nome} ${o.cognome || ""}`.trim();
    const ds = dogsByOwner(o.id);
    return ds.length ? `${nm} (${ds.join(", ")})` : nm;
  };
  const filteredOwners = owners.filter((o) => {
    const q = ownerQuery.trim().toLowerCase();
    if (!q) return true;
    const nm = `${o.nome} ${o.cognome || ""}`.toLowerCase();
    return nm.includes(q) || dogsByOwner(o.id).some((n) => n.toLowerCase().includes(q));
  });
  const selectedOwner = owners.find((o) => o.id === ownerId);

  const onOwner = (v) => { setOwnerId(v); setDogIds([]); setLines((ls) => ls.map((l) => ({ ...l, dogs: [] }))); };

  const addDogToAllLines = (id) =>
    setLines((ls) => ls.map((l) => (l.service_type_id && !l.dogs.some((d) => d.dog_id === id)
      ? { ...l, dogs: [...l.dogs, { dog_id: id, applied_price: l.list_price || 0, coverage: (l.package_eligible && l.price_scope === "per_dog") ? defaultCoverage(id, l.default_credits || 1) : "direct", coverage_auto: true, required_credits: l.default_credits || 1 }] }
      : l)));

  const toggleDog = (id) => {
    if (dogIds.includes(id)) {
      setDogIds((v) => v.filter((x) => x !== id));
      setLines((ls) => ls.map((l) => ({ ...l, dogs: l.dogs.filter((d) => d.dog_id !== id) })));
      return;
    }
    const hasConfigured = lines.some((l) => l.service_type_id);
    setDogIds((v) => [...v, id]);
    if (hasConfigured) setApplyPrompt({ dogId: id });
    else setLines((ls) => ls.map((l) => (l.dogs.some((d) => d.dog_id === id) ? l : { ...l, dogs: [...l.dogs, { dog_id: id, applied_price: l.list_price || 0, coverage: "direct", coverage_auto: true, required_credits: l.default_credits || 1 }] })));
  };

  const confirmApplyAll = (yes) => {
    if (applyPrompt && yes) addDogToAllLines(applyPrompt.dogId);
    setApplyPrompt(null);
  };

  const patchLine = (id, patch) => setLines((ls) => ls.map((l) => (l.uid === id ? { ...l, ...patch } : l)));
  const applyService = (id, t) => setLines((ls) => ls.map((l) => (l.uid === id ? { ...mkLineFromType(t, dogIds), uid: id, expanded: true } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine(dogIds)]);
  const removeLine = (id) => setLines((ls) => ls.filter((l) => l.uid !== id));
  const toggleLineDog = (lid, did) => setLines((ls) => ls.map((l) => {
    if (l.uid !== lid) return l;
    const has = l.dogs.some((d) => d.dog_id === did);
    return has
      ? { ...l, dogs: l.dogs.filter((d) => d.dog_id !== did) }
      : { ...l, dogs: [...l.dogs, { dog_id: did, applied_price: l.list_price || 0, coverage: (l.package_eligible && l.price_scope === "per_dog") ? defaultCoverage(did, l.default_credits || 1) : "direct", coverage_auto: true, required_credits: l.default_credits || 1 }] };
  }));
  const patchLineDog = (lid, did, patch) => setLines((ls) => ls.map((l) => (l.uid === lid ? { ...l, dogs: l.dogs.map((d) => (d.dog_id === did ? { ...d, ...patch } : d)) } : l)));

  // BUG-02: keep the automatic package/direct default consistent whenever its dependencies
  // (resolved packages, selected dogs, line structure) change — regardless of field order.
  // Only auto-mode values are recomputed; manual overrides (auto flag=false) are preserved.
  // Returns the same array ref when nothing changes, so this never loops or thrashes state.
  useEffect(() => {
    if (readOnly) return;
    setLines((ls) => recomputeLinesCoverage(ls, pkgByDog));
  }, [pkgByDog, dogIds, lines, readOnly]);

  const totals = useMemo(() => {
    let ref = 0, pkg = 0, gift = 0, direct = 0;
    const credMap = {};
    for (const l of lines) {
      if (!l.service_type_id) continue;
      const s = splitLine(l);
      ref += s.ref; pkg += s.pkg; gift += s.gift; direct += s.direct;
      for (const [did, c] of s.creds) if (did && c > 0) credMap[did] = (credMap[did] || 0) + c;
    }
    return { ref: round2(ref), pkg: round2(pkg), gift: round2(gift), direct: round2(direct), credMap };
  }, [lines]);

  const parentMin = useMemo(() => parentDuration(lines), [lines]);
  const timedUids = lines.filter((l) => l.service_type_id && l.has_duration && lineMinutes(l) > 0).map((l) => l.uid);
  const baseUid = timedUids[0];

  const orario = useMemo(() => {
    if (!date) return "";
    const start = new Date(`${date}T${startTime || "00:00"}:00`);
    if (isNaN(start.getTime())) return "";
    const end = new Date(start.getTime() + parentMin * 60000);
    const f = (dt, withTime) => `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}${withTime ? ` ${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}` : ""}`;
    if (!startTime) return `${fmtDate(date)}${parentMin ? ` · durata ${durLabel(parentMin)}` : ""}`;
    return `${f(start, true)} → ${f(end, true)}`;
  }, [date, startTime, parentMin]);

  const buildLines = () => lines.filter((l) => l.service_type_id).map((l) => {
    const base = {
      service_type_id: l.service_type_id, service_name: l.service_name,
      price_scope: l.price_scope, pricing_mode: l.pricing_mode, billing_unit: l.billing_unit,
      has_duration: l.has_duration, duration_minutes: lineMinutes(l), duration_unit: l.duration_unit,
      timing: l.timing, package_eligible: l.package_eligible, default_credits: l.default_credits,
      professional_diary_enabled: l.professional_diary_enabled, list_price: Number(l.list_price || 0),
    };
    if (l.price_scope === "per_dog") {
      base.dogs = l.dogs.map((d) => ({ dog_id: d.dog_id, applied_price: Number(d.applied_price || 0), price_override: d.price_override == null ? null : Number(d.price_override), coverage: d.coverage, required_credits: d.coverage === "package" ? Number(d.required_credits || 1) : 0 }));
    } else {
      base.applied_price = Number(l.applied_price || 0);
      base.price_override = l.price_override == null ? null : Number(l.price_override);
      base.line_coverage = l.line_coverage;
      base.required_credits = l.line_coverage === "package" ? Number(l.line_required_credits || 1) : 0;
      base.dogs = l.dogs.map((d) => ({ dog_id: d.dog_id }));
    }
    return base;
  });

  const submit = async () => {
    if (!ownerId) return toast.error("Seleziona un proprietario.");
    if (!dogIds.length) return toast.error("Seleziona almeno un cane.");
    const configured = lines.filter((l) => l.service_type_id);
    if (!configured.length) return toast.error("Aggiungi almeno un servizio.");
    if (configured.some((l) => !l.dogs.length)) return toast.error("Ogni servizio deve essere associato ad almeno un cane.");
    if (!configured.some((l) => l.has_duration && lineMinutes(l) > 0)) return toast.error("Aggiungi almeno un servizio con durata.");
    for (const l of configured) {
      const prices = l.price_scope === "per_dog" ? l.dogs : [l];
      if (l.pricing_mode === "per_time_unit" && prices.some((p) => p.applied_price === "" || !Number.isFinite(Number(p.applied_price)) || Number(p.applied_price) < 0)) {
        return toast.error("Inserisci una tariffa valida, maggiore o uguale a zero.");
      }
      if (prices.some((p) => p.price_override != null && (p.price_override === "" || !Number.isFinite(Number(p.price_override)) || Number(p.price_override) < 0))) {
        return toast.error("Inserisci un prezzo personalizzato valido, maggiore o uguale a zero.");
      }
      const pkgDogs = l.price_scope === "per_dog"
        ? l.dogs.filter((d) => d.coverage === "package")
        : (l.line_coverage === "package" ? l.dogs.slice(0, 1) : []);
      for (const d of pkgDogs) {
        const need = Number((l.price_scope === "per_dog" ? d.required_credits : l.line_required_credits) || 1);
        const p = activePkg(d.dog_id);
        if (!p || (p.remaining_credits || 0) < need) {
          return toast.error(`Pacchetto non disponibile per ${dogName(d.dog_id)}: scegli pagamento diretto o omaggio, oppure aggiungi un pacchetto.`);
        }
      }
    }
    if (savingRef.current) return;
    savingRef.current = true; setSaving(true);
    try {
      const payload = {
        owner_id: ownerId, dog_ids: dogIds, service_type: compositeLabel(lines),
        date, start_time: startTime || "", duration_minutes: parentMin || 60,
        status, location: "", notes: notes || "", value: 0, package_linked: false,
        credits: 0, is_free: false, request_key: reqKey, service_lines: buildLines(),
      };
      if (isEdit) await api.put(`/sessions/${editSession.id}`, payload);
      else await api.post("/sessions", payload);
      toast.success(isEdit ? "Prestazione aggiornata" : "Prestazione creata");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      setReqKey(uid());
      toast.error(apiErr(e));
    } finally {
      setSaving(false); savingRef.current = false;
    }
  };

  const title = readOnly ? "Prestazione svolta" : isEdit ? "Modifica prestazione" : mode === "lezione" ? "Registra lezione" : "Nuova prestazione";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto wg-scroll" data-testid="v2-prestation-dialog">
        <DialogHeader><DialogTitle className="font-head">{title}</DialogTitle>
          <DialogDescription className="sr-only">Crea o modifica una prestazione con uno o più servizi e cani.</DialogDescription>
        </DialogHeader>

        {readOnly ? (
          <ReadOnlyView lines={lines} dogName={dogName} totals={totals} orario={orario} />
        ) : (
          <div className="space-y-4">
            {/* Owner */}
            <div className="space-y-1">
              <Label>Proprietario *</Label>
              {(presetOwnerId || isEdit) ? (
                <div data-testid="v2-owner" className="rounded-md border border-input bg-secondary/40 px-3 py-2 text-sm font-medium">{selectedOwner ? ownerLabel(selectedOwner) : "—"}</div>
              ) : (
                <div className="relative">
                  <button type="button" data-testid="v2-owner" onClick={() => setOwnerOpen((v) => !v)}
                    className="flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm">
                    <span className={selectedOwner ? "" : "text-muted-foreground"}>{selectedOwner ? ownerLabel(selectedOwner) : "Cerca per proprietario o cane…"}</span>
                    <ChevronDown className="h-4 w-4 opacity-60 shrink-0" />
                  </button>
                  {ownerOpen && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-md">
                      <div className="flex items-center gap-2 border-b border-border px-2 py-1.5">
                        <Search className="h-4 w-4 opacity-60" />
                        <input autoFocus data-testid="v2-owner-search" value={ownerQuery} onChange={(e) => setOwnerQuery(e.target.value)} placeholder="Cerca proprietario o cane…" className="w-full bg-transparent text-sm outline-none" />
                      </div>
                      <div className="max-h-56 overflow-y-auto py-1">
                        {filteredOwners.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">Nessun risultato.</div>}
                        {filteredOwners.map((o) => (
                          <button key={o.id} type="button" data-testid={`v2-owner-opt-${o.id}`} onClick={() => { onOwner(o.id); setOwnerOpen(false); setOwnerQuery(""); }}
                            className={`block w-full px-3 py-2 text-left text-sm hover:bg-accent ${o.id === ownerId ? "bg-accent" : ""}`}>{ownerLabel(o)}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Dogs */}
            {ownerId && (
              <div className="space-y-1">
                <Label>Cani *</Label>
                <div className="flex flex-wrap gap-2" data-testid="v2-dogs">
                  {dogsForOwner.map((d) => {
                    const active = dogIds.includes(d.id);
                    return (
                      <button key={d.id} type="button" data-testid={`v2-dog-${d.id}`} onClick={() => toggleDog(d.id)}
                        className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border"}`}>
                        <PawPrint className="h-3.5 w-3.5" />{d.nome}
                      </button>
                    );
                  })}
                  {dogsForOwner.length === 0 && <span className="text-sm text-muted-foreground">Nessun cane per questo proprietario.</span>}
                </div>
              </div>
            )}

            {/* Date + time */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Data</Label>
                <Input type="date" data-testid="v2-date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
              <div className="space-y-1"><Label>Ora inizio</Label>
                <TimeField value={startTime} onChange={setStartTime} testid="v2-time" /></div>
            </div>

            {/* Lines */}
            <div className="space-y-2">
              <Label>Servizi</Label>
              {lines.map((l, idx) => (
                <LineCard key={l.uid} line={l} idx={idx} types={types} dogIds={dogIds} dogName={dogName}
                  isBase={l.uid === baseUid} canRemove={lines.length > 1} activePkg={activePkg}
                  onApplyService={(t) => applyService(l.uid, t)}
                  onPatch={(p) => patchLine(l.uid, p)} onRemove={() => removeLine(l.uid)}
                  onToggleDog={(did) => toggleLineDog(l.uid, did)} onPatchDog={(did, p) => patchLineDog(l.uid, did, p)} />
              ))}
              <Button type="button" variant="outline" data-testid="v2-add-line" onClick={addLine} className="w-full rounded-xl border-dashed"><Plus className="h-4 w-4 mr-1" />Aggiungi servizio</Button>
            </div>

            {/* Status */}
            <div className="space-y-1"><Label>Stato</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="v2-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div className="space-y-1"><Label>Note</Label>
              <Textarea data-testid="v2-notes" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>

            <Summary totals={totals} orario={orario} dogName={dogName} />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{readOnly ? "Chiudi" : "Annulla"}</Button>
          {!readOnly && <Button data-testid="v2-save" onClick={submit} disabled={saving}>{saving ? "Salvataggio…" : isEdit ? "Salva modifiche" : "Crea prestazione"}</Button>}
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={!!applyPrompt} onOpenChange={(v) => !v && setApplyPrompt(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Applicare i servizi già presenti anche a {applyPrompt ? dogName(applyPrompt.dogId) : ""}?</AlertDialogTitle>
            <AlertDialogDescription>Puoi aggiungere questo cane a tutti i servizi già inseriti, oppure scegliere tu servizio per servizio.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="v2-apply-all-no" onClick={() => confirmApplyAll(false)}>No, scelgo io</AlertDialogCancel>
            <AlertDialogAction data-testid="v2-apply-all-yes" onClick={() => confirmApplyAll(true)}>Sì, a tutti</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export const PriceEditor = ({ line, price, minutes, testid, onChange }) => {
  const manual = price.price_override != null;
  const calculated = svcValue(price.applied_price, line.pricing_mode, line.billing_unit, minutes);
  const catalogue = svcValue(line.list_price, line.pricing_mode, line.billing_unit, minutes);
  const unit = line.pricing_mode === "per_time_unit" ? BILLING_LABEL[line.billing_unit] : null;
  const legacyRate = Number(price.applied_price) !== Number(line.list_price);
  return <div className="space-y-2">
    <p className="text-xs text-muted-foreground">{unit ? "Per durata" : "Prezzo fisso"} · {line.price_scope === "per_dog" ? "per cane" : "per prestazione"} · Tariffa salvata {fmtEUR(price.applied_price)}{unit ? " / " + unit : ""}</p>
    <Label htmlFor={testid}>{unit && !manual ? "Tariffa applicata € / " + unit : "Prezzo totale applicato €"} {line.price_scope === "per_dog" ? "(questo cane)" : "(intera riga)"}</Label>
    <Input id={testid} type="number" min="0" step="0.01" data-testid={testid} value={manual ? price.price_override : unit ? price.applied_price : calculated} onFocus={(e) => e.target.select()} onChange={(e) => onChange(unit && !manual ? { applied_price: e.target.value, price_override: null } : { price_override: e.target.value })} />
    {unit && !manual && <p className="text-xs text-muted-foreground" data-testid={testid + "-total"}>Totale calcolato: {fmtEUR(calculated)}</p>}
    {manual && <p className="text-xs font-medium" role="status">Prezzo personalizzato: resta invariato se cambia la durata. Calcolato dalla tariffa salvata: {fmtEUR(calculated)}{round2(price.price_override) !== calculated ? " (diverso dal prezzo applicato)" : ""}.</p>}
    {legacyRate && <p className="text-xs text-muted-foreground">Tariffa personalizzata. Listino salvato: {fmtEUR(line.list_price)}{unit ? " / " + unit : ""}.</p>}
    {(manual || legacyRate) && <Button type="button" variant="outline" size="sm" className="h-auto whitespace-normal text-left" data-testid={testid + "-restore"} onClick={() => onChange({ price_override: null, applied_price: line.list_price })}>{unit ? "Ripristina tariffa salvata · " + fmtEUR(line.list_price) + " / " + unit : "Ripristina prezzo calcolato · " + fmtEUR(catalogue)}</Button>}
  </div>;
};
const LineCard = ({ line: l, idx, types, dogIds, dogName, isBase, canRemove, activePkg, onApplyService, onPatch, onRemove, onToggleDog, onPatchDog }) => {
  const s = splitLine(l);
  const min = lineMinutes(l);
  const configured = !!l.service_type_id;
  const dogNames = l.dogs.map((d) => dogName(d.dog_id)).filter(Boolean).join(", ");
  const timed = l.has_duration && min > 0;
  const pkgAllowedPerBooking = l.package_eligible && l.dogs.length === 1;

  return (
    <div className="rounded-2xl border border-border bg-card" data-testid={`v2-line-${idx}`}>
      {/* Header / collapsed */}
      <div className="flex items-center gap-2 p-3">
        <button type="button" data-testid={`v2-line-toggle-${idx}`} onClick={() => onPatch({ expanded: !l.expanded })} className="flex-1 min-w-0 text-left">
          {configured ? (
            <>
              <div className="font-semibold truncate">{l.service_name}{isBase && timed ? "" : ""}</div>
              <div className="text-xs text-muted-foreground truncate">
                {timed ? `${durLabel(min)} · ` : ""}{dogNames || "nessun cane"}{s.ref ? ` · ${fmtEUR(s.ref)}` : ""}
              </div>
            </>
          ) : <div className="text-sm text-muted-foreground">Scegli un servizio…</div>}
        </button>
        {canRemove && <Button type="button" variant="ghost" size="sm" data-testid={`v2-line-remove-${idx}`} onClick={onRemove} className="text-destructive shrink-0"><Trash2 className="h-4 w-4" /></Button>}
        <button type="button" onClick={() => onPatch({ expanded: !l.expanded })} className="shrink-0 text-muted-foreground">{l.expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>
      </div>

      {l.expanded && (
        <div className="border-t border-border p-3 space-y-3">
          {/* Service picker */}
          <div className="space-y-1">
            <Label className="text-xs">Servizio</Label>
            <Select value={l.service_type_id} onValueChange={(v) => { const t = types.find((x) => x.id === v); if (t) onApplyService(t); }}>
              <SelectTrigger data-testid={`v2-line-service-${idx}`}><SelectValue placeholder="Seleziona servizio" /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {configured && (<>
            {/* Duration */}
            {l.has_duration && (
              <div className="flex items-end gap-2">
                <div className="space-y-1 flex-1"><Label className="text-xs">Durata</Label>
                  <Input type="number" min="1" inputMode="numeric" data-testid={`v2-line-dur-${idx}`} value={l.dur_value}
                    onFocus={(e) => e.target.select()} onChange={(e) => onPatch({ dur_value: e.target.value })} /></div>
                <div className="space-y-1 w-28"><Label className="text-xs">Unità</Label>
                  <Select value={l.duration_unit} onValueChange={(v) => onPatch({ duration_unit: v, dur_value: "" })}>
                    <SelectTrigger data-testid={`v2-line-dur-unit-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minutes">Minuti</SelectItem>
                      <SelectItem value="hours">Ore</SelectItem>
                      <SelectItem value="days">Giorni</SelectItem>
                    </SelectContent>
                  </Select></div>
              </div>
            )}

            {/* Timing (only non-base timed lines) */}
            {timed && !isBase && (
              <div className="space-y-1"><Label className="text-xs">Orario</Label>
                <Select value={l.timing} onValueChange={(v) => onPatch({ timing: v })}>
                  <SelectTrigger data-testid={`v2-line-timing-${idx}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="included">Compreso nell'orario della prestazione</SelectItem>
                    <SelectItem value="extends">Estende la durata</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {timed && isBase && <p className="text-[11px] text-muted-foreground">Questo servizio definisce l'orario della prestazione.</p>}

            {/* Dog subset */}
            <div className="space-y-1">
              <Label className="text-xs">Cani coinvolti</Label>
              <div className="flex flex-wrap gap-2">
                {dogIds.map((did) => {
                  const active = l.dogs.some((d) => d.dog_id === did);
                  return (
                    <button key={did} type="button" data-testid={`v2-line-dog-${idx}-${did}`} onClick={() => onToggleDog(did)}
                      className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${active ? "bg-primary text-primary-foreground border-primary" : "bg-secondary border-border"}`}>{dogName(did)}</button>
                  );
                })}
              </div>
            </div>

            {/* Pricing + coverage */}
            {l.price_scope === "per_dog" ? (
              <div className="space-y-2">
                {l.dogs.map((d) => {
                  return (
                    <div key={d.dog_id} className="rounded-xl border border-border p-2.5 space-y-2 bg-secondary/20">
                      <div className="text-sm font-semibold">{dogName(d.dog_id)}</div>
                      <PriceEditor line={l} price={d} minutes={min} testid={`v2-line-price-${idx}-${d.dog_id}`} onChange={(patch) => onPatchDog(d.dog_id, patch)} />
                      <CoveragePicker testidBase={`v2-line-cov-${idx}-${d.dog_id}`} value={d.coverage} pkgAllowed={l.package_eligible} onChange={(v) => onPatchDog(d.dog_id, { coverage: v, coverage_auto: false })} />
                      {d.coverage === "package" && (<>
                        <div className="flex items-center gap-2"><Label className="text-[11px] text-muted-foreground">Crediti</Label>
                          <Input type="number" min="1" className="w-20 h-8" data-testid={`v2-line-credits-${idx}-${d.dog_id}`} value={d.required_credits} onChange={(e) => onPatchDog(d.dog_id, { required_credits: e.target.value })} /></div>
                        <PkgPreview pkg={activePkg(d.dog_id)} credits={Number(d.required_credits || 1)} testid={`v2-line-pkg-${idx}-${d.dog_id}`} />
                      </>)}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-xl border border-border p-2.5 space-y-2 bg-secondary/20">
                <PriceEditor line={l} price={l} minutes={min} testid={`v2-line-price-${idx}`} onChange={onPatch} />
                <CoveragePicker testidBase={`v2-line-cov-${idx}`} value={l.line_coverage} pkgAllowed={pkgAllowedPerBooking} onChange={(v) => onPatch({ line_coverage: v, line_coverage_auto: false })} />
                {!pkgAllowedPerBooking && l.package_eligible && l.dogs.length > 1 && (
                  <p className="text-[11px] text-muted-foreground">La copertura con pacchetto su prezzo unico è disponibile solo con un singolo cane.</p>
                )}
                {l.line_coverage === "package" && (<>
                  <div className="flex items-center gap-2"><Label className="text-[11px] text-muted-foreground">Crediti</Label>
                    <Input type="number" min="1" className="w-20 h-8" data-testid={`v2-line-credits-${idx}`} value={l.line_required_credits} onChange={(e) => onPatch({ line_required_credits: e.target.value })} /></div>
                  <PkgPreview pkg={activePkg(l.dogs[0]?.dog_id)} credits={Number(l.line_required_credits || 1)} testid={`v2-line-pkg-${idx}`} />
                </>)}
              </div>
            )}
          </>)}
        </div>
      )}
    </div>
  );
};

const CoveragePicker = ({ value, onChange, pkgAllowed, testidBase }) => (
  <div className="flex flex-wrap gap-1.5">
    {COVERAGES.filter(([v]) => v !== "package" || pkgAllowed).map(([v, label, Icon]) => (
      <button key={v} type="button" data-testid={`${testidBase}-${v}`} onClick={() => onChange(v)}
        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${value === v ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border"}`}>
        <Icon className="h-3 w-3" />{label}
      </button>
    ))}
  </div>
);

const PkgPreview = ({ pkg, credits, testid }) => (
  <div className="text-[11px] mt-1" data-testid={testid}>
    {pkg
      ? <span className="text-[#4B6B58]">Pacchetto {fmtEUR(pkg.total_price)} · {pkg.remaining_credits} crediti residui · Usa {credits} {creditWord(credits)}</span>
      : <span className="text-[#C62828]">Nessun pacchetto disponibile per questo cane.</span>}
  </div>
);

const Summary = ({ totals, orario, dogName }) => {
  const credEntries = Object.entries(totals.credMap).filter(([, c]) => c > 0);
  return (
    <div className="rounded-2xl border border-[#B7D3C4] bg-[#E6EFEA] p-4 space-y-2" data-testid="v2-summary">
      <div className="font-head font-bold text-sm text-[#234735]">Riepilogo</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <span className="text-muted-foreground">Valore servizi</span><span className="text-right font-medium">{fmtEUR(totals.ref)}</span>
        {totals.pkg > 0 && (<><span className="text-muted-foreground">Coperto da pacchetti</span><span className="text-right font-medium">{fmtEUR(totals.pkg)}</span></>)}
        {totals.gift > 0 && (<><span className="text-muted-foreground">Omaggio</span><span className="text-right font-medium">{fmtEUR(totals.gift)}</span></>)}
        <span className="font-semibold text-[#234735]">Da incassare</span><span className="text-right font-bold text-[#234735]" data-testid="v2-summary-due">{fmtEUR(totals.direct)}</span>
      </div>
      {credEntries.length > 0 && (
        <div className="text-xs text-[#4B6B58] border-t border-[#B7D3C4] pt-2">
          <div className="font-semibold mb-0.5">Crediti previsti</div>
          {credEntries.map(([did, c]) => <div key={did} className="flex justify-between"><span>{dogName(did)}</span><span>{c} {creditWord(c)}</span></div>)}
        </div>
      )}
      {orario && <div className="text-xs text-[#4B6B58] border-t border-[#B7D3C4] pt-2"><span className="font-semibold">Orario: </span>{orario}</div>}
    </div>
  );
};

const ReadOnlyView = ({ lines, dogName, totals, orario }) => (
  <div className="space-y-3">
    <p className="text-sm text-muted-foreground">Questa prestazione è stata svolta. La struttura e gli importi sono in sola lettura.</p>
    {lines.map((l, idx) => {
      const s = splitLine(l);
      const min = lineMinutes(l);
      return (
        <div key={l.uid} className="rounded-2xl border border-border bg-card p-3" data-testid={`v2-ro-line-${idx}`}>
          <div className="font-semibold">{l.service_name}</div>
          <div className="text-xs text-muted-foreground">{l.has_duration && min > 0 ? `${durLabel(min)} · ` : ""}{l.dogs.map((d) => dogName(d.dog_id)).filter(Boolean).join(", ")} · {fmtEUR(s.ref)}</div>
          {l.price_scope === "per_dog"
            ? l.dogs.map((d) => <div key={d.dog_id} className="text-xs text-muted-foreground mt-1">{dogName(d.dog_id)}: {fmtEUR(appliedAmount(l, d, min))}{d.price_override != null ? " · Prezzo personalizzato" : ""} · {d.coverage === "package" ? "Pacchetto" : d.coverage === "gift" ? "Omaggio" : "Pagamento diretto"}</div>)
            : <div className="text-xs text-muted-foreground mt-1">{l.price_override != null ? "Prezzo personalizzato · " : ""}{l.line_coverage === "package" ? "Pacchetto" : l.line_coverage === "gift" ? "Omaggio" : "Pagamento diretto"}</div>}
        </div>
      );
    })}
    <Summary totals={totals} orario={orario} dogName={dogName} />
  </div>
);
